import { apiFetch } from './http';

/** Whether this account has a watcher token and whether the platform publishes a binary at all. */
export interface WatcherToken {
  hasToken: boolean;
  published: boolean;
  version: string | null;
}

/** One watcher as its last heartbeat left it: who can lend a machine now. */
export interface WatcherPresence {
  accountId: number;
  handle: string;
  lastSeenAt: string;
  gameRunning: boolean;
  lendable: boolean;
  version: string | null;
  hotkey: boolean;
  present: boolean;
  /** The Arma 3 DLCs the machine's Steam account owns (platform keys: apex, contact, gm…); null when the watcher never said. */
  ownedDlcs?: string[] | null;
  /** How the watcher knows: 'steam' (asked Steam) or 'declared' (the lender's own list). */
  dlcSource?: string | null;
  /** Whether the watcher is holding the PC awake (no idle sleep, no idle lock); null when it did not say (an older watcher). */
  keepAwake?: boolean | null;
  /** Whether the machine's Windows session is locked or disconnected: no game can be opened on it. Null when unknown. */
  locked?: boolean | null;
}

export interface WatcherJob {
  id: number;
  accountId: number;
  kind: 'connect' | 'disconnect';
  sessionCode: string | null;
  payload: unknown;
  state: 'pending' | 'taken' | 'done' | 'failed' | 'cancelled';
  createdAt: string;
  takenAt: string | null;
  doneAt: string | null;
  ok: boolean | null;
  detail: string | null;
}

export const fetchWatcherToken = () => apiFetch<WatcherToken>('/api/watcher/token');
export const regenerateWatcherToken = () =>
  apiFetch<WatcherToken>('/api/watcher/token', { method: 'POST' });
export const fetchWatcherPresence = () => apiFetch<WatcherPresence[]>('/api/watcher/presence');
export const queueWatcherJob = (body: {
  accountId: number;
  sessionCode?: string;
  kind: 'connect' | 'disconnect';
}) => apiFetch<WatcherJob>('/api/watcher/jobs', { method: 'POST', body });
export const fetchWatcherJobs = () => apiFetch<WatcherJob[]>('/api/watcher/jobs');

/**
 * A player's own connect job (never another account's machine): opens the
 * watcher's game into a session the caller can see, the same job kind a
 * lend task queues, just for oneself rather than a lent machine. Delivery
 * still waits for the watcher's own "es pot deixar" at the next heartbeat.
 */
export const queueMyWatcherJob = (sessionCode: string) =>
  apiFetch<WatcherJob>('/api/watcher/jobs/mine', { method: 'POST', body: { sessionCode } });
/** The state of one's own job, to poll while it is pending or taken. */
export const fetchMyWatcherJob = (id: number) =>
  apiFetch<WatcherJob>(`/api/watcher/jobs/mine/${id}`);

/** The download is a plain link: the browser saves the personalised binary. */
export const WATCHER_DOWNLOAD_URL = '/api/watcher/download';
